import React, { useEffect, useRef, useState, useCallback, useMemo, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Link, Tooltip, Fade, SxProps } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { NodeResizeControl } from '@reactflow/node-resizer';
import { I18N_KEYS } from '@/language/keys';
import { DEFAULT_NODE_WIDTH, DEFAULT_RESIZABLE_DIMENSIONS, type NodeResizeLimits } from '@/consts/node-ui.consts';
import { FlexCenVer, FlexColCenHorVer } from '@/UI/styles';
import { useDebugContext } from '@/hooks/useDebugContext';
import { useEdges, useFlowView, useNodeNullable, useEffectiveIsLocked } from '@/components/Recipe/FlowContext';
import { color, EL_COLORS } from '@/colors';
import { HandleType } from '@/enums/handle-type.enum';
import { useConnectionContext } from '@/components/Recipe/FlowComponents/FlowTour/ConnectionContext';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useWorkflowStore, useUserWorkflowRole } from '@/state/workflow.state';
import { FF_KEY_NAVIGATION } from '@/consts/featureFlags';
import { getHandleId } from '../Utils';
import { MenuAction } from '../../Menu/Actions';
import { NavigationKeyBadge } from '../NavigationKeyBadge';
import { DynamicNodeHandles } from './DynamicNodeHandles';
import { DynamicNodeTitle } from './DynamicNodeTitle';
import { DynamicNodeDebugWindow } from './NodeDebugWindow';
import { handleTypeForKey } from './HandlesUtils';
import type { OnResize, OnResizeEnd, OnResizeStart } from '@reactflow/node-resizer';
import type { BaseNodeData, HandleSide, Handle } from '@/types/node';

const TRANSITION_DURATION = '0.1s';
const NODE_TRANSITION = `background ${TRANSITION_DURATION} ease-in-out, border ${TRANSITION_DURATION} ease-in-out, filter ${TRANSITION_DURATION} ease-in-out, border-color ${TRANSITION_DURATION} ease-in-out`;

function useLockedTooltip({
  nodeRef,
  isLocked,
  role,
}: {
  nodeRef: React.RefObject<HTMLDivElement | null>;
  id: string;
  isLocked?: boolean;
  role: 'guest' | 'editor' | null;
}) {
  const [showIsLockedTooltip, setShowIsLockedTooltip] = useState(false);
  const [, setInteractionWhileLockedCount] = useState(0);
  const handleLockedInteraction = useCallback(() => {
    if (isLocked && role !== 'guest') {
      setInteractionWhileLockedCount((prev) => {
        const newCount = prev + 1;
        if (newCount === 4) {
          setShowIsLockedTooltip(true);
          return 0; // Reset counter after showing tooltip
        }
        return newCount;
      });
    }
  }, [isLocked, role]);

  const handleTooltipClose = useCallback(() => {
    setShowIsLockedTooltip(false);
  }, []);

  useEffect(() => {
    if (!isLocked) {
      setShowIsLockedTooltip(false);
    }
  }, [isLocked]);

  // Add handlers for both click and mousedown (drag attempt)
  useEffect(() => {
    const nodeElement = nodeRef.current;
    if (nodeElement) {
      nodeElement.addEventListener('mousedown', handleLockedInteraction);

      return () => {
        nodeElement.removeEventListener('mousedown', handleLockedInteraction);
      };
    }
  }, [handleLockedInteraction, nodeRef]);

  return { showIsLockedTooltip, handleTooltipClose };
}

interface DynamicNodeChildProps {
  isSelected?: boolean;
}

type DynamicNode2Props = PropsWithChildren<{
  additionalMenu?: MenuAction[];
  className?: string;
  data: BaseNodeData & { model?: { name?: string } };
  displayName?: string;
  handleColor?: string;
  hideBody?: boolean;
  hideHandles?: boolean;
  hideTitle?: boolean;
  icon?: React.ReactNode;
  id: string;
  inputHandleYPos?: string;
  menuHeader?: React.ReactNode;
  onRename?: (newName: string) => void;
  outputHandleYPos?: string;
  overrideShowFullNode?: boolean;
  isResizable?: boolean;
  resizableDimensions?: NodeResizeLimits;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps;
  titleRight?: React.ReactNode;
}>;

export const DynamicNode2 = ({
  additionalMenu,
  children,
  className,
  data,
  displayName,
  handleColor,
  hideBody,
  hideHandles = false,
  hideTitle = false,
  icon,
  id,
  inputHandleYPos,
  menuHeader,
  onRename,
  outputHandleYPos,
  overrideShowFullNode = false,
  isResizable = false,
  resizableDimensions = DEFAULT_RESIZABLE_DIMENSIONS,
  size = 'large',
  sx,
  titleRight,
}: DynamicNode2Props) => {
  const role = useUserWorkflowRole();

  const { t } = useTranslation();

  const ff_isKeyNavigationEnabled = useFeatureFlagEnabled(FF_KEY_NAVIGATION);
  // get the current node type / remove this after rollout
  const node = useNodeNullable(id);
  const currentNodeType = node?.type;
  const nodeHeight = (node?.data as { height?: number })?.height;

  const { isHovered, ...elementProps } = useIsHovered();

  const isSelected = node?.selected;

  const showFullNode = useMemo(() => isSelected || overrideShowFullNode, [isSelected, overrideShowFullNode]);

  const { handles, version } = data;

  // Required inputs validation
  const edges = useEdges();
  const nodeInputEdges = useMemo(() => {
    return edges.filter((edge) => edge.target === id).map((edge) => edge.id);
  }, [edges, id]);
  const prevNodeInputEdges = useRef<string[]>(nodeInputEdges);

  const missingRequiredInputs = useWorkflowStore(
    useShallow((state) => state.nodeValidation[id]?.missingRequiredInputs?.extraInfo.keys),
  );

  const emptyRequiredInputs = useWorkflowStore(
    useShallow((state) => state.nodeValidation[id]?.emptyRequiredInputs?.extraInfo.keys),
  );

  const removeNodeValidationByType = useWorkflowStore((state) => state.removeNodeValidationByType);

  useEffect(() => {
    const sortedPrevNodeInputEdges = sortBy(prevNodeInputEdges.current);
    const sortedNodeInputEdges = sortBy(nodeInputEdges);
    if (isEqual(sortedPrevNodeInputEdges, sortedNodeInputEdges)) return;
    prevNodeInputEdges.current = nodeInputEdges;
    // recheck validation on input change
    removeNodeValidationByType(id, 'missingRequiredInputs');
    removeNodeValidationByType(id, 'emptyRequiredInputs');
  }, [id, nodeInputEdges, removeNodeValidationByType]);

  // node locked indication
  const nodeRef = useRef<HTMLDivElement>(null);

  const { IS_DEBUG } = useDebugContext();

  const flowView = useFlowView();
  const isParentLocked = useEffectiveIsLocked(node?.parentId);
  const selectNodeById = useCallback((nodeId: string) => (flowView.selectedNodes = [nodeId]), [flowView]);
  const handleClickOnNode = useCallback(() => selectNodeById(id), [id, selectNodeById]);
  const handleToggleLock = useCallback(() => {
    if (isParentLocked) {
      flowView.toggleLockNode(node?.parentId as string);
    } else {
      flowView.toggleLockNode(id);
    }
  }, [id, flowView, isParentLocked, node?.parentId]);

  const isLocked = useEffectiveIsLocked(id);
  const { showIsLockedTooltip, handleTooltipClose } = useLockedTooltip({ nodeRef, id, isLocked, role });

  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback<OnResizeStart>(() => {
    setIsResizing(true);
  }, []);

  const handleResize = useCallback<OnResize>(
    (_, params) => {
      flowView.resizeNode(id, { height: params.height }, true);
    },
    [flowView, id],
  );

  const handleResizeEnd = useCallback<OnResizeEnd>(
    (_, params) => {
      setIsResizing(false);
      flowView.resizeNode(id, { height: params.height }, false);
    },
    [flowView, id],
  );

  const handleResizeDoubleClick = useCallback(() => {
    flowView.resizeNode(id, { height: undefined }, false);
  }, [flowView, id]);

  const copyId = useCallback(() => {
    void navigator.clipboard.writeText(id);
  }, [id]);

  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);

  const handleExternalClose = useCallback(() => {
    setContextMenuPosition(null);
  }, []);

  useEffect(() => {
    if (!contextMenuPosition) return;
    const closeMenu = (e: MouseEvent) => {
      e.preventDefault();
      setContextMenuPosition(null);
    };
    document.addEventListener('contextmenu', closeMenu, true);
    return () => document.removeEventListener('contextmenu', closeMenu, true);
  }, [contextMenuPosition]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (role !== 'editor' || hideTitle) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(
          'button, input, textarea, [role="slider"], .dynamic-node-title-label, [contenteditable], canvas, .media-container-dark, .node-text-viewer, .node-crop-area, .import-node-content',
        )
      )
        return;
      e.preventDefault();
      setContextMenuPosition({ x: e.clientX, y: e.clientY });
    },
    [role, hideTitle],
  );

  // sort handles by its order
  const sortedInputHandles = useMemo(() => {
    const inputHandles = handles?.input;
    return inputHandles && !Array.isArray(inputHandles)
      ? Object.entries(inputHandles).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
      : inputHandles || [];
  }, [handles?.input]);

  const sortedOutputHandles = useMemo(() => {
    const outputHandles = handles?.output;
    return outputHandles && !Array.isArray(outputHandles)
      ? Object.entries(outputHandles).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
      : outputHandles || [];
  }, [handles?.output]);

  const { currentConnection } = useConnectionContext();

  /// hide irrelevant handles when dragging a new edge
  const shouldObscureHandle = useCallback(
    (handleType: HandleType, handleId: string, handleSide: HandleSide) => {
      if (currentConnection?.nodeId === id && currentConnection?.handleSide !== handleSide) return true; // obscure - same node other side
      if (currentConnection?.handleId === handleId) return false; // dont obscure from the current handle
      if (currentConnection?.handleSide === handleSide) return true; // obscure all handles that are on the same side - target cannot connect to target
      if (handleType === HandleType.Any || currentConnection?.handleType === HandleType.Any) return false; // if not type, don't obscure
      return Boolean(
        // obscure all handles that are not the same type and not the same handle
        currentConnection?.handleType &&
        currentConnection.handleType !== handleType &&
        currentConnection.handleId !== handleId,
      );
    },
    [currentConnection, id],
  );

  // Figma "Node + edges states": default White4 border + Black92; selected White64 border + Black88.
  const headerBorder = `2px solid ${isSelected ? color.White64_T : EL_COLORS.BoxBorder}`;

  const shouldObscureNode = useMemo(() => {
    if (
      !currentConnection ||
      !currentConnection.handleType ||
      currentConnection.handleType === HandleType.Any ||
      currentConnection.nodeId === id
    ) {
      return false;
    }

    // Get all input and output handles
    const allInputHandles = sortedInputHandles.map((item: string | [string, Handle]) => {
      const key = typeof item === 'string' ? item : item[0];
      return {
        handleId: getHandleId(id, 'input', key),
        handleType: handleTypeForKey(handles, 'input', key, version),
        handleSide: 'target' as HandleSide,
      };
    });

    const allOutputHandles = sortedOutputHandles.map((item: string | [string, Handle]) => {
      const key = typeof item === 'string' ? item : item[0];
      return {
        handleId: getHandleId(id, 'output', key),
        handleType: handleTypeForKey(handles, 'output', key, version),
        handleSide: 'source' as HandleSide,
      };
    });

    // Check if all handles should be obscured
    const allHandlesObscured = [...allInputHandles, ...allOutputHandles].every(
      ({ handleId, handleType, handleSide }) =>
        handleType !== HandleType.Any && shouldObscureHandle(handleType, handleId, handleSide),
    );

    // Only set obscureNode to true if there are handles and all are obscured
    return (allInputHandles.length > 0 || allOutputHandles.length > 0) && allHandlesObscured;
  }, [currentConnection, id, shouldObscureHandle, sortedInputHandles, sortedOutputHandles, handles, version]);

  const headerFilter = shouldObscureNode ? 'brightness(0.4)' : 'none';

  return (
    <>
      <Tooltip
        title={
          showIsLockedTooltip ? (
            isParentLocked ? (
              <FlexCenVer>
                {`${t(I18N_KEYS.RECIPE_MAIN.NODES.LOCKED.THIS_NODE_IS_LOCKED_GROUP)}`}
                &nbsp;
                <Link
                  onClick={handleToggleLock}
                >{`${t(I18N_KEYS.RECIPE_MAIN.NODES.LOCKED.CLICK_TO_UNLOCK_GROUP)}`}</Link>
              </FlexCenVer>
            ) : (
              <FlexCenVer>
                {`${t(I18N_KEYS.RECIPE_MAIN.NODES.LOCKED.THIS_NODE_IS_LOCKED)}`}
                &nbsp;
                <Link onClick={handleToggleLock}>{`${t(I18N_KEYS.RECIPE_MAIN.NODES.LOCKED.CLICK_TO_UNLOCK)}`}</Link>
              </FlexCenVer>
            )
          ) : (
            ''
          )
        }
        onMouseDown={handleClickOnNode}
        placement="top"
        open={showIsLockedTooltip}
        onClose={handleTooltipClose}
        leaveDelay={500}
        slots={{ transition: Fade }}
      >
        <FlexColCenHorVer
          {...elementProps}
          ref={nodeRef}
          onContextMenu={handleContextMenu}
          className={`${className || ''} node-header`}
          sx={{
            position: 'relative',
            cursor: role === 'guest' || !showFullNode ? 'default' : 'grab',
            minHeight: isResizable ? resizableDimensions.minHeight : 50,
            width: size === 'small' ? 250 : DEFAULT_NODE_WIDTH,
            background: isSelected ? color.Black88 : color.Black92,
            border: headerBorder,
            transition: isResizing ? 'none' : NODE_TRANSITION,
            p: 2,
            filter: headerFilter,
            borderRadius: size === 'small' ? 2 : 4,
            ...(isResizable
              ? {
                  ...(nodeHeight ? { height: Math.min(nodeHeight, resizableDimensions.maxHeight) } : {}),
                  maxHeight: nodeHeight ? undefined : resizableDimensions.autoResizeMaxHeight,
                }
              : {}),
            // Stacking context + opaque shell when selected so SVG edges don’t read through during drag.
            ...(isSelected ? { opacity: 1, isolation: 'isolate' } : {}),
            ...(sx || {}),
          }}
        >
          {ff_isKeyNavigationEnabled && <NavigationKeyBadge nodeId={id} />}
          {IS_DEBUG ? (
            <Box sx={{ cursor: 'copy', mt: 1 }} onClick={copyId}>
              ID: {id} version 2 {currentNodeType}
            </Box>
          ) : null}
          {!hideTitle && (
            <DynamicNodeTitle
              additionalMenu={additionalMenu}
              data={data}
              externalOpenPosition={contextMenuPosition}
              displayName={displayName}
              hideBody={hideBody}
              icon={icon}
              id={id}
              menuHeader={menuHeader}
              onExternalClose={handleExternalClose}
              onRename={onRename}
              showFullNode={showFullNode}
              titleRight={titleRight}
            />
          )}

          {!hideBody && (
            <Box
              className="node-content"
              sx={{
                width: '100%',
                position: 'relative',
                cursor: showFullNode ? '' : 'pointer',
                ...(isResizable
                  ? {
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: 0,
                      willChange: isResizing ? 'transform' : undefined,
                    }
                  : {}),
              }}
            >
              {React.Children.map(children, (child) => {
                if (!React.isValidElement(child)) {
                  return child;
                }
                if (typeof child.type === 'function') {
                  return React.cloneElement(child as React.ReactElement<DynamicNodeChildProps>, {
                    isSelected: true,
                  });
                }
                return child;
              })}
            </Box>
          )}
          {!hideHandles && (
            <DynamicNodeHandles
              handleColor={handleColor}
              handles={handles}
              id={id}
              inputHandleYPos={inputHandleYPos}
              isHovered={isHovered}
              isSelected={isSelected}
              nodeType={currentNodeType}
              outputHandleYPos={outputHandleYPos}
              shouldObscureHandle={shouldObscureHandle}
              showFullNode={showFullNode}
              sortedInputHandles={sortedInputHandles as [string, Handle][]}
              sortedOutputHandles={sortedOutputHandles as [string, Handle][]}
              invalidHandles={{
                required: missingRequiredInputs,
                requiredEmpty: emptyRequiredInputs,
              }}
              version={version}
            />
          )}
          {IS_DEBUG && <DynamicNodeDebugWindow data={data} />}
          {isResizable && !isLocked && (
            <NodeResizeControl
              nodeId={id}
              position="bottom"
              minHeight={resizableDimensions.minHeight}
              maxHeight={resizableDimensions.maxHeight}
              style={{
                background: 'transparent',
                borderColor: 'transparent',
                borderBottomWidth: '10px',
                width: '100%',
              }}
              onResizeStart={handleResizeStart}
              onResize={handleResize}
              onResizeEnd={handleResizeEnd}
            >
              <Box onDoubleClick={handleResizeDoubleClick} sx={{ width: '100%', height: 10 }} />
            </NodeResizeControl>
          )}
        </FlexColCenHorVer>
      </Tooltip>
    </>
  );
};
